import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import * as BuilderUI from '../../../../../../components/Builder/BuilderUI';
import { FauxInput } from '../../../../../../components/Form';
import { useAppDispatch, useAppSelector } from '../../../../../../redux/hooks';
import { IBinning } from '../../../../../../redux/slices/oldBuilder/binning/types';
import {
  IRowMeta,
  removeBinning,
  removeErrorAction,
  RowType,
  setSelectedBinning,
} from '../../../../../../redux/slices/oldBuilder';
import {
  BucketDefType,
  BucketKind,
  Maybe,
} from '../../../../../../utils/apollo/graphqlSchema';
import {
  BinningPopover,
  convertToManualRangeBinning,
  isValidAutoRangeBucketInput,
  isValidBinning,
} from '../../../../../../components/Builder/BuilderFields/BuilderBinning';
import { HighlightedEntity } from '../../../../../../components/HighlightingContext';
import HistogramBinningDisplay from './HistogramBinningDisplay';
import { errorPrimitiveSelector } from '../../../../../../redux/selectors/builder';
import KubitTooltip from '../../../../../../components/KubitTooltip';
import { builderDefaultBinningSelector } from '../../../../../../redux/selectors/binning';
import {
  KubitPopover,
  KubitPopoverContent,
  KubitPopoverTrigger,
} from '../../../../../../components/KubitPopover';

const rowType = RowType.footer;

// only enable range binning for now until bugfix on subject key field query
const supportedBucketKinds = [BucketKind.Range];

const Container = styled(HighlightedEntity)`
  position: relative;
  display: flex;
  align-items: center;
  padding-top: 4px;
  padding-bottom: 4px;
  padding-left: 8px;
  border-radius: 4px;
`;

const HistogramBinning = () => {
  /**
   * @Brett: Logic on Cached data and redux data
   * I don’t want to pass the setDraftedState callback from top to bottom all the time.
   * Therefore, I used CachedBinning to save the data on opening the window.
   * restore the data from cachedBinning to redux.binning when clicking Cancel.
   */
  const [cachedBinning, setCachedBinning] = useState<Maybe<IBinning>>(null);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  const dispatch = useAppDispatch();

  const selectedBinning = useAppSelector(builderDefaultBinningSelector);

  const rowMeta: IRowMeta = {
    rowType,
    rowIndex: 0,
  };

  const errorSelector = useMemo(errorPrimitiveSelector, []);
  const errors = useAppSelector((state) => errorSelector(state, rowMeta, ''));

  /**
   * Once user click confirm, we set cached binning to (default)
   * Once user click cancel, we set cached binning to redux.binning to restore the states.
   */

  const handlePopoverOpen = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event?.defaultPrevented) {
      return;
    }

    // store binning into cache to allow fallback if cancel
    setCachedBinning(selectedBinning);
  };

  const handlePopoverClose = () => {
    setIsPopoverOpen(false);
    // restore the drafted selection
    if (cachedBinning) {
      dispatch(
        setSelectedBinning({
          newBinning: cachedBinning,
        }),
      );
    } else {
      dispatch(removeBinning());
    }
  };

  const handleSubmit = () => {
    dispatch(removeErrorAction({ rowMeta, errorKey: '' }));

    // if current binning has a valid Auto Range Binning, parse it into manual input.
    if (
      selectedBinning &&
      selectedBinning.type === BucketDefType.Basic &&
      selectedBinning.kind === BucketKind.Range &&
      selectedBinning.isAutoRangeBucket
    ) {
      const autoRangeBucket = selectedBinning.autoRangeBucket;
      if (
        isValidAutoRangeBucketInput(
          autoRangeBucket.min,
          autoRangeBucket.max,
          autoRangeBucket.binCount,
        )
      ) {
        dispatch(
          setSelectedBinning({
            newBinning: convertToManualRangeBinning(selectedBinning, false),
          }),
        );
      }
    }

    setCachedBinning(null);
    setIsPopoverOpen(false);
  };

  const removeHistogramInput = () => {
    dispatch(removeBinning());
    setCachedBinning(null);
  };

  const binningToDisplay = useMemo(() => {
    if (isPopoverOpen) {
      if (cachedBinning) {
        return cachedBinning;
      }

      return null;
    }

    return selectedBinning;
  }, [cachedBinning, isPopoverOpen, selectedBinning]);

  const isValidHistogramInput = isValidBinning(selectedBinning);

  return (
    <Container>
      <KubitTooltip
        content={`Group values inside of the breakdown into groups or "bins".`}
        actionText="Learn More"
        actionUrl="https://docs.kubit.ai/docs/query#additional-bin-options"
        noOpenDelay={true}
      >
        <BuilderUI.BuilderFieldLabel>Binning</BuilderUI.BuilderFieldLabel>
      </KubitTooltip>
      <KubitPopover isOpen={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
        <KubitPopoverTrigger asChild={true}>
          <FauxInput onClick={handlePopoverOpen} hasError={errors}>
            {binningToDisplay ? (
              <HistogramBinningDisplay
                selectedBinning={binningToDisplay}
                onClear={removeHistogramInput}
              />
            ) : null}
          </FauxInput>
        </KubitPopoverTrigger>
        <KubitPopoverContent>
          <BinningPopover
            isOpen={isPopoverOpen}
            onClose={handlePopoverClose}
            type="histogramBinning"
            supportedBucketKinds={supportedBucketKinds}
            defaultNamedBucketDef={
              cachedBinning?.type === BucketDefType.Named
                ? cachedBinning.namedBucketDef
                : null
            }
            validBinning={isValidHistogramInput}
            onSubmit={handleSubmit}
          />
        </KubitPopoverContent>
      </KubitPopover>
    </Container>
  );
};

export default HistogramBinning;
